import React, { useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@altimateai/lego";
import { BaseDetailsProps } from "../types";
import { InformationTab } from "./InformationTab";
import Activity from "./Activity";
import { SummaryTab } from "./SummaryTab";
import { ColumnSection } from "@/pages/query/components/SideContent";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import { useInsightState } from "../context";
import { SqlLineageNodeDetail } from "@/pages/query/components/QuerySqlLineage";

interface DetailsTabProps extends BaseDetailsProps {
  showScheduledEventsActivity?: boolean;
  opportunity: GetOpportunitiesDataResponse;
}

export const DetailsTab: React.FC<DetailsTabProps> = props => {
  const { sqlLineage } = useInsightState();

  const columns = useMemo(() => {
    if (!sqlLineage) return [];
    const [nodeKey = ""] =
      Object.entries(sqlLineage.query_tags_meta).find(([, tags]) =>
        tags.some(t => t.opportunity_id === props.opportunity.opportunity_id)
      ) ?? [];
    if (!nodeKey) return [];
    const nodeDetail = sqlLineage.details?.[nodeKey] as SqlLineageNodeDetail | undefined;
    return (
      nodeDetail?.columns?.map(item => ({
        ...item,
        description: item.expression,
      })) ?? []
    );
  }, [sqlLineage, props.opportunity.opportunity_id]);

  return (
    <Tabs defaultValue="information" variant={"underlined"}>
      <TabsList>
        <TabsTrigger value="information">Information</TabsTrigger>
        {props.opportunity.explanation_context ? (
          <TabsTrigger value="summary">Summary</TabsTrigger>
        ) : null}
        {sqlLineage && <TabsTrigger value="columns">Columns</TabsTrigger>}
        <TabsTrigger value="activity">Activity</TabsTrigger>
      </TabsList>

      <TabsContent value="information" className="al-mt-2">
        <InformationTab {...props} />
      </TabsContent>

      <TabsContent value="summary">
        <SummaryTab opportunity={props.opportunity} />
      </TabsContent>
      {sqlLineage && (
        <TabsContent value="columns" className="al-mt-2">
          <ColumnSection columns={columns} />
        </TabsContent>
      )}
      <TabsContent value="activity" className="al-mt-2">
        <Activity
          opportunity_id={props.opportunity.opportunity_id}
          showScheduledEventsActivity={props.showScheduledEventsActivity}
        />
      </TabsContent>
    </Tabs>
  );
};
